import { RefObject, useCallback, useEffect, useRef } from 'react'
import { useIsomorphicLayoutEffect } from './hooks/useIsomorphicLayoutEffect'

const INTERSECTION_THRESHOLD = 0.5

export function useIntersectionObserver<T extends Element>(
  domNodeRef: RefObject<T | null>,
  onChange: (
    domNode: T | null,
    intersecting: boolean,
    ratio: number,
    unobserve: () => void,
  ) => void,
  intersectionThreshHold = INTERSECTION_THRESHOLD,
) {
  const intersecting = useRef(false)
  const observer = useRef(
    process.browser
      ? new IntersectionObserver(
          entries => {
            entries.map(entry => {
              const isIntersecting =
                entry.isIntersecting &&
                entry.intersectionRatio > intersectionThreshHold

              if (intersecting.current !== isIntersecting) {
                intersecting.current = isIntersecting
                onChange(
                  domNodeRef.current,
                  isIntersecting,
                  entry.intersectionRatio,
                  unobserve,
                )
              }
            })
          },
          { threshold: [intersectionThreshHold] },
        )
      : null,
  )

  const unobserve = useCallback(() => {
    if (domNodeRef.current && observer.current) {
      observer.current.unobserve(domNodeRef.current)
      observer.current = null
    }
  }, [domNodeRef, observer])

  useIsomorphicLayoutEffect(() => {
    if (domNodeRef.current && observer.current) {
      observer.current.observe(domNodeRef.current)
    }

    return () => {
      if (domNodeRef.current && observer.current) {
        observer.current.unobserve(domNodeRef.current)
      }
    }
  }, [domNodeRef.current, observer.current])

  useEffect(() => unobserve, [])

  return { unobserve }
}
